import { RawDraftContentState } from 'draft-js';

import { DatasetType, SingleDatasetFieldType } from '../../../datasets/store';
import { DateFilter, PreExecutionFilterOption } from '../../../documents/search/store/types';
import { PieChartAnalyticsData, GraphKeyAndColour } from '../../../shared/analytics/PieChart';
import { DropdownOption } from '../../../shared/dropdown/Dropdown';

export enum PortfolioAnalyticsActionTypes {
    UPDATE_PORTFOLIO_QUESTION = 'UPDATE_PORTFOLIO_QUESTION',
    SET_PORTFOLIO_FIELD = 'SET_PORTFOLIO_FIELD',
    FETCH_DOCUMENT_FIELDS_STARTED = 'FETCH_DOCUMENT_FIELDS_STARTED',
    FETCH_DOCUMENT_FIELDS_SUCCESSFUL = 'FETCH_DOCUMENT_FIELDS_SUCCESSFUL',
    FETCH_DOCUMENT_FIELDS_FAILED = 'FETCH_DOCUMENT_FIELDS_FAILED',
    ASK_PORTFOLIO_QUESTION_STARTED = 'ASK_PORTFOLIO_QUESTION_STARTED',
    ASK_PORTFOLIO_QUESTION_SUCCESSFUL = 'ASK_PORTFOLIO_QUESTION_SUCCESSFUL',
    ASK_PORTFOLIO_QUESTION_FAILED = 'ASK_PORTFOLIO_QUESTION_FAILED',
    REDIRECT_TO_DOCUMENT = 'REDIRECT_TO_DOCUMENT',
    RESET_PORTFOLIO_SURVEY = 'RESET_PORTFOLIO_SURVEY',
    SET_IS_CHANGING_PORTFOLIO_SURVEY_QUESTION = 'SET_IS_CHANGING_PORTFOLIO_SURVEY_QUESTION',
    CLEAR_PORTFOLIO_RESULTS = 'CLEAR_PORTFOLIO_RESULTS',
    SET_FILE_NAME = 'SET_PORTFOLIO_ANALYTICS_FILE_NAME',
    DOWNLOAD_PORTFOLIO_ANALYTICS_STARTED = 'DOWNLOAD_PORTFOLIO_ANALYTICS_STARTED',
    DOWNLOAD_PORTFOLIO_ANALYTICS_SUCCESSFUL = 'DOWNLOAD_PORTFOLIO_ANALYTICS_SUCCESSFUL',
    DOWNLOAD_PORTFOLIO_ANALYTICS_FAILED = 'DOWNLOAD_PORTFOLIO_ANALYTICS_FAILED',
    EMAIL_PORTFOLIO_ANALYTICS_STARTED = 'EMAIL_PORTFOLIO_ANALYTICS_STARTED',
    EMAIL_PORTFOLIO_ANALYTICS_SUCCESSFUL = 'EMAIL_PORTFOLIO_ANALYTICS_SUCCESSFUL',
    EMAIL_PORTFOLIO_ANALYTICS_FAILED = 'EMAIL_PORTFOLIO_ANALYTICS_FAILED',
    SET_KEY_COLOUR_PAIR = 'SET_PORTFOLIO_ANALYTICS_KEY_COLOUR_PAIR',
    SET_PIE_CHART_BASE_64 = 'SET_PORTFOLIO_ANALYTICS_PIE_CHART_BASE_64',
    TOGGLE_DOWNLOAD_MODAL = 'TOGGLE_PORTFOLIO_ANALYTICS_DOWNLOAD_MODAL',
    TOGGLE_EMAIL_MODAL = 'TOGGLE_PORTFOLIO_ANALYTICS_EMAIL_MODAL',
    UPDATE_PORTFOLIO_ANALYTICS_EMAIL_VALUE = 'UPDATE_PORTFOLIO_ANALYTICS_EMAIL_VALUE',
    SET_SURVEY_SUMMARY = 'SET_PORTFOLIO_ANALYTICS_SURVEY_SUMMARY',
    SET_PORTFOLIO_ANALYSIS_PAGE_VIEW = 'SET_PORTFOLIO_ANALYSIS_PAGE_VIEW',
    GET_ALL_PORTFOLIO_ANALYTICS_TEMPLATES_STARTED = 'GET_ALL_PORTFOLIO_ANALYTICS_TEMPLATES_STARTED',
    GET_ALL_PORTFOLIO_ANALYTICS_TEMPLATES_SUCCESSFUL = 'GET_ALL_PORTFOLIO_ANALYTICS_TEMPLATES_SUCCESSFUL',
    GET_ALL_PORTFOLIO_ANALYTICS_TEMPLATES_FAILED = 'GET_ALL_PORTFOLIO_ANALYTICS_TEMPLATES_FAILED',
    SET_SURVEY_INDEX = 'SET_PORTFOLIO_ANALYTICS_SURVEY_INDEX',
    SET_PORTFOLIO_QUESTION = 'SET_PORTFOLIO_QUESTION',
    DELETE_PORTFOLIO_ANALYTICS_TEMPLATE_STARTED = 'DELETE_PORTFOLIO_ANALYTICS_TEMPLATE_STARTED',
    DELETE_PORTFOLIO_ANALYTICS_TEMPLATE_SUCCESSFUL = 'DELETE_PORTFOLIO_ANALYTICS_TEMPLATE_SUCCESSFUL',
    DELETE_PORTFOLIO_ANALYTICS_TEMPLATE_FAILED = 'DELETE_PORTFOLIO_ANALYTICS_TEMPLATE_FAILED',
    SET_PORTFOLIO_ANALYTICS_TEMPLATE = 'SET_PORTFOLIO_ANALYTICS_TEMPLATE',
    SAVE_PORTFOLIO_ANALYTICS_TEMPLATE_STARTED = 'SAVE_PORTFOLIO_ANALYTICS_TEMPLATE_STARTED',
    SAVE_PORTFOLIO_ANALYTICS_TEMPLATE_SUCCESSFUL = 'SAVE_PORTFOLIO_ANALYTICS_TEMPLATE_SUCCESSFUL',
    SAVE_PORTFOLIO_ANALYTICS_TEMPLATE_FAILED = 'SAVE_PORTFOLIO_ANALYTICS_TEMPLATE_FAILED',
    UPDATE_PORTFOLIO_ANALYTICS_DATE_FILTER = 'UPDATE_PORTFOLIO_ANALYTICS_DATE_FILTER',
    UPDATE_PORTFOLIO_ANALYTICS_FILTER = 'UPDATE_PORTFOLIO_ANALYTICS_FILTER'
}

export enum PortfolioAnalyticsSurveyIndex {
    AGREEMENT_TYPE = 0,
    DOCUMENT_NAMES = 1,
    DATASET_FIELD = 2,
    USER_QUESTION = 3
}

export enum PortfolioAnalysisPage {
    SELECT = 'Select',
    TEMPLATES = 'Portfolio Analysis Templates',
    SURVEY = 'Portfolio Analysis Survey',
    RESULTS = 'Portfolio Analysis Results'
}

export interface PortfolioAnalyticsReportEmail extends DownloadMeta {
    filename: string;
    userIds: number[];
    emailBody: RawDraftContentState | null;
}

export interface PortfolioQuestionFilters {
    documentNameIds: number[];
    entityIds?: string[];
    includePreExecution?: PreExecutionFilterOption;
    date?: DateFilter;
    isExclusiveEntityFilter?: boolean;
}

export interface PortfolioQuestion {
    parentDatasetId: number | null;
    datasetType: DatasetType;
    fieldId: string | null;
    fieldLabel: string;
    fieldType: SingleDatasetFieldType | null;
    sectionId: string | null;
    fieldDatasetId: number | null;
    groupFieldId?: string;
    fieldIndex: number | null;
    groupFieldIndex?: number;
    question: string;
    filters: PortfolioQuestionFilters;
}

export interface PortfolioDocumentField {
    id: string;
    label: string;
    datasetId: number;
    datasetType: DatasetType;
    datasetTitle: string;
    parentFieldId: string;
    type: SingleDatasetFieldType;
    sectionId: string;
    groupFieldId?: string;
    fieldIndex: number;
    groupFieldIndex?: number;
    dropdownLinked?: string | null;
    isMultiSelect?: boolean;
}

export interface PortfolioResponse {
    value: string | string[][];
    documentDescription: string;
    documentId: number;
    datasetInstanceId: number;
    partyA: string;
    partyB: string;
    graphKey: string | string[];
}

export interface PortfolioQuestionResponse {
    graphData: PieChartAnalyticsData[];
    results: PortfolioResponse[];
    summary: string;
}

export interface PortfolioAnalyticsSurveyQuestion {
    question: string;
    selectedOption?: DropdownOption | DropdownOption[] | null;
    options?: DropdownOption[];
    key: keyof PortfolioQuestion;
    userQuestion?: string;
}

export interface DownloadMeta {
    svgId: string;
    width: number;
    height: number;
}

export interface PortfolioAnalysisSurveySummary {
    agreementType: string;
    documentNames: string[];
    fieldLabel: string;
    questionAsked: string;
}

export interface PortfolioAnalyticsDownloadEmailParams {
    data: PortfolioResponse[];
    graphData: PieChartAnalyticsData[];
    agreementType: string;
    documentNames: string;
    fieldLabel: string;
    question: string;
    summary: string;
    doubleRowHeight: boolean;
    pieChartBase64: string;
    fieldType: SingleDatasetFieldType;
    isForm: boolean;
}

export interface PortfolioAnalysisTemplateRequest {
    portfolioAnalysisTemplateId?: number;
    templateName: string;
    metaData: PortfolioQuestion;
}

export interface PortfolioAnalysisTemplate extends PortfolioAnalysisTemplateRequest {
    portfolioAnalysisTemplateId: number;
    createdBy: string;
    createdDate: string;
    lastAskedBy: string;
    lastAskedDate: string;
}

export interface PortfolioAnalyticsState {
    portfolioQuestion: PortfolioQuestion;
    availableFields: PortfolioDocumentField[];
    questionResponse: PortfolioQuestionResponse | null;
    isAskingQuestion: boolean;
    isChangingQuestion: boolean;
    downloadFilename: string | null;
    graphKeyColours: GraphKeyAndColour;
    pieChartBase64: string;
    isDownloading: boolean;
    reportEmail: PortfolioAnalyticsReportEmail | null;
    surveySummary: PortfolioAnalysisSurveySummary;
    pageView: PortfolioAnalysisPage;
    templates: PortfolioAnalysisTemplate[];
    surveyIndex: number;
    templateUsed: PortfolioAnalysisTemplate | null;
    isFetchingTemplates: boolean;
}
